import Button from '@components/Button';
import { Collapse } from '@components/Collapse';
import ColorPicker from '@components/ColorPicker';
import FeatureToggle from '@components/FeatureToggle';
import Slider from '@components/Slider';
import Tabbar from '@components/Tabbar';
import Textarea from '@components/Textarea';
import useTextareaHandelrs from '@hooks/useTextareaHandelrs';
import {
  $GateMapMarkerActive,
  $GateMapMarkerTip,
  $editingMapMarkerApi,
} from '@stores/editingMarkerStores';
import {
  type STMapMarker,
  type STMapMarkerCircle,
  STMapMarkerType,
} from '@types';
import colorStringToHexAndAlpha from '@utils/colorStringToHexAndAlpha';
import { hexToColorString } from '@utils/hexToColorString';
import { useGate } from 'effector-react';
import type { Draft } from 'immer';
import { flagToBool, toFlag } from 'pano-utils';
import { useEffect, useMemo, useState } from 'react';

import useConstant from 'use-constant';
import { stEditorAmap } from '../STAMap';

interface Props {
  marker: STMapMarkerCircle;
  isNew: boolean;
}

const defaultFillColor = hexToColorString({ hex: '#286EFA', alpha: 0.2 });
const defaultFillColorActive = hexToColorString({ hex: '#286EFA', alpha: 0.5 });
const defaultBorderColor = hexToColorString({ hex: '#286EFA', alpha: 1 });

export function FeatureSettingsCircle({ marker, isNew }: Props) {
  const { blink, borderStyle, fillStyle, borderStyleActive, fillStyleActive } =
    marker.icon;
  const [isActive, setIsActive] = useState(false);
  const [isEditing, setIsEditing] = useState(isNew);
  const editor = useConstant(
    () => new AMap.CircleEditor(stEditorAmap.getMap())
  );
  const [title, setTitle] = useState(marker.title);

  useGate($GateMapMarkerActive, isActive);
  useGate(
    $GateMapMarkerTip,
    isEditing
      ? ['按住边框的圆点拖拽调整大小', '按住中心的圆点并拖动可改变整体位置']
      : []
  );

  useEffect(() => {
    const handleEvent = (event: any) => {
      updateMarker((draft) => {
        draft.icon.radius = event.target.getRadius();
        draft.icon.lng = event.target.getCenter().lng;
        draft.icon.lat = event.target.getCenter().lat;
      });
    };
    editor.on('move', handleEvent);
    editor.on('adjust', handleEvent);
    editor.on('end', handleEvent);
    return () => {
      editor.off('move', handleEvent);
      editor.off('adjust', handleEvent);
      editor.off('end', handleEvent);
      editor.close();
    };
  }, []);

  useEffect(() => {
    if (isEditing) {
      stEditorAmap.addMarker(marker, { isEditing });
      const circle = stEditorAmap.getMarkerObjectById(marker.id);
      if (circle) {
        editor.setTarget(circle as AMap.Circle);
        editor.open();
      }
    } else {
      editor.close();
    }
  }, [isEditing]);

  const borderColor = useMemo(() => {
    return hexToColorString({
      hex: borderStyle.color,
      alpha: borderStyle.opacity,
    });
  }, [borderStyle]);

  const borderColorActive = useMemo(() => {
    return hexToColorString({
      hex: borderStyleActive.color,
      alpha: borderStyleActive.opacity,
    });
  }, [borderStyleActive]);

  const fillColor = useMemo(() => {
    return hexToColorString({ hex: fillStyle.color, alpha: fillStyle.opacity });
  }, [fillStyle]);

  const fillColorActive = useMemo(() => {
    return hexToColorString({
      hex: fillStyleActive.color,
      alpha: fillStyleActive.opacity,
    });
  }, [fillStyleActive]);

  useEffect(() => {
    if (isEditing) return;
    stEditorAmap.addMarker(marker);
    // if (isActive) {
    //   stEditorAmap.addMarker(
    //     produce(marker, (draft) => {
    //       draft.icon.borderStyle = draft.icon.borderStyleActive;
    //       draft.icon.fillStyle = draft.icon.fillStyleActive;
    //     })
    //   );
    // } else {
    //   stEditorAmap.addMarker(marker);
    // }
  }, [marker, isEditing]);

  const updateMarker = (
    updater: (draft: Draft<STMapMarkerCircle>) => STMapMarker | void
  ) => {
    $editingMapMarkerApi.update((draft) => {
      draft.type ??= STMapMarkerType.circle;
      if (draft.type !== STMapMarkerType.circle) return;
      updater(draft);
    });
  };

  const handlers = useTextareaHandelrs('trimRight', (val) => {
    updateMarker((draft) => {
      draft.title = val;
    });
  });

  const renderEditing = () => {
    return (
      <div className="px-10 py-15 bg-[#282828] rounded">
        请在地图中进行圆形标记的编辑操作
        <Button
          className="w-full mt-20"
          onClick={() => {
            setIsEditing(false);
          }}
        >
          完成编辑
        </Button>
      </div>
    );
  };

  const renderSettings = () => {
    return (
      <>
        <Collapse title="圆形样式">
          <Tabbar
            tabs={['默认状态', '移入状态']}
            activeIndex={isActive ? 1 : 0}
            onChange={(index) => {
              setIsActive(index === 1);
            }}
          />
          <ColorPicker
            className="mt-20"
            colorString={isActive ? fillColorActive : fillColor}
            onChange={(colorString) => {
              const { hex, alpha } = colorStringToHexAndAlpha(colorString);
              if (isActive) {
                updateMarker((draft) => {
                  draft.icon.fillStyleActive.color = hex;
                  draft.icon.fillStyleActive.opacity = alpha;
                });
              } else {
                updateMarker((draft) => {
                  draft.icon.fillStyle.color = hex;
                  draft.icon.fillStyle.opacity = alpha;
                });
              }
            }}
          >
            <ColorPicker.Header>
              填充颜色
              <ColorPicker.Reset
                defaultColor={
                  isActive ? defaultFillColorActive : defaultFillColor
                }
              />
            </ColorPicker.Header>
            <ColorPicker.Bar />
          </ColorPicker>
          <ColorPicker
            className="mt-20"
            colorString={isActive ? borderColorActive : borderColor}
            onChange={(colorString) => {
              const { hex, alpha } = colorStringToHexAndAlpha(colorString);
              if (isActive) {
                updateMarker((draft) => {
                  draft.icon.borderStyleActive.color = hex;
                  draft.icon.borderStyleActive.opacity = alpha;
                });
              } else {
                updateMarker((draft) => {
                  draft.icon.borderStyle.color = hex;
                  draft.icon.borderStyle.opacity = alpha;
                });
              }
            }}
          >
            <ColorPicker.Header>
              边框颜色
              <ColorPicker.Reset defaultColor={defaultBorderColor} />
            </ColorPicker.Header>
            <ColorPicker.Bar />
          </ColorPicker>
          <Slider
            className="mt-20"
            min={1}
            max={20}
            value={isActive ? borderStyleActive.width : borderStyle.width}
            valToString={(val) => `${val}px`}
            onChange={(val) => {
              if (isActive) {
                updateMarker((draft) => {
                  draft.icon.borderStyleActive.width = val;
                });
              } else {
                updateMarker((draft) => {
                  draft.icon.borderStyle.width = val;
                });
              }
            }}
          >
            <Slider.Header title="边框粗细" />
            <Slider.Bar />
            <Slider.Footer />
          </Slider>
          <FeatureToggle
            title="默认/移入状态交替变化"
            className="mb-20"
            isOn={flagToBool(blink)}
            onToggle={(isOn) => {
              updateMarker((draft) => {
                draft.icon.blink = toFlag(isOn);
              });
            }}
          />
          <Button
            className="w-full mt-20"
            onClick={() => {
              setIsEditing(true);
            }}
          >
            编辑圆形
          </Button>
        </Collapse>
        <Collapse title="标记标题">
          <Textarea
            value={title}
            scheme="dark"
            className="mb-20 h-115"
            placeholder="请输入标记标题"
            maxLength={500}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={handlers.onBlur}
          />
        </Collapse>
      </>
    );
  };

  return isEditing ? renderEditing() : renderSettings();
}
